import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Sparkles, Calendar, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HomeBlockRenderer from '../components/HomeBlockRenderer';
import EditablePageIntro from '../components/EditablePageIntro';
import { splitSpecialties } from '../utils/specialty';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', interest: 'partnership' });
    const [submitting, setSubmitting] = useState(false);
    const [siteSettings, setSiteSettings] = useState(null);
    const [homeBlocks, setHomeBlocks] = useState([]);
    // Featured pillar of the month (rotates via backend based on year+month; may be null on 404)
    const [featuredPillar, setFeaturedPillar] = useState(null);

    React.useEffect(() => {
        Promise.all([
            axios.get(`${API_URL}/api/site-settings`),
            axios.get(`${API_URL}/api/home-blocks`)
        ]).then(([sRes, bRes]) => {
            setSiteSettings(sRes.data);
            setHomeBlocks(bRes.data?.blocks || []);
            if (sRes.data?.home_page_slug) {
                navigate(`/p/${sRes.data.home_page_slug}`, { replace: true });
            }
        }).catch(() => {});
        // Fetch the pillar of the month — silently no-op if there are no non-admin partners yet.
        axios.get(`${API_URL}/api/pillars/featured`)
            .then(res => setFeaturedPillar(res.data))
            .catch(() => setFeaturedPillar(null));
        const onChange = () => {
            axios.get(`${API_URL}/api/site-settings`).then(res => setSiteSettings(res.data)).catch(() => {});
            axios.get(`${API_URL}/api/home-blocks`).then(res => setHomeBlocks(res.data?.blocks || [])).catch(() => {});
        };
        window.addEventListener('site-settings-changed', onChange);
        window.addEventListener('home-blocks-changed', onChange);
        return () => {
            window.removeEventListener('site-settings-changed', onChange);
            window.removeEventListener('home-blocks-changed', onChange);
        };
    }, [navigate]);

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/contact`, contactForm);
            toast.success('Thank you for reaching out. We will contact you soon.');
            setContactForm({ name: '', email: '', message: '', interest: 'partnership' });
        } catch (error) {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&q=80"
                        alt="Monastery architecture"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[#1A1A2E]/50"></div>
                </div>

                {/* Content */}
                <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
                    <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light text-[#F9F6F0] leading-tight mb-8 animate-fade-in" data-testid="hero-headline">
                        {siteSettings?.hero_heading || 'Remember Who You Are.'}
                    </h1>
                    <p className="font-sans text-lg sm:text-xl text-[#F9F6F0]/90 leading-relaxed max-w-2xl mx-auto mb-12 animate-fade-in animate-delay-1" data-testid="hero-subheadline">
                        {siteSettings?.hero_subheading || 'You are not broken. You are not lost. You are in the middle of something your soul designed. And what feels like the worst of it, is closest to your greatest gift.'}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in animate-delay-2">
                        <Link 
                            to={isAuthenticated ? "/dashboard" : "/register"} 
                            className="btn-primary"
                            data-testid="hero-cta"
                        >
                            Enter the Circle
                        </Link>
                        <Link 
                            to="/events" 
                            className="btn-secondary !border-[#F9F6F0] !text-[#F9F6F0] bg-[#1A1A2E]/30 backdrop-blur-sm hover:!bg-[#F9F6F0] hover:!text-[#1A1A2E] flex items-center gap-2"
                            data-testid="hero-events"
                        >
                            <Calendar className="w-4 h-4" />
                            Explore Events
                        </Link>
                        <Link 
                            to="/library" 
                            className="btn-secondary !border-[#F9F6F0] !text-[#F9F6F0] bg-[#1A1A2E]/30 backdrop-blur-sm hover:!bg-[#F9F6F0] hover:!text-[#1A1A2E] flex items-center gap-2"
                            data-testid="hero-library"
                        >
                            <BookOpen className="w-4 h-4" />
                            Browse Library
                        </Link>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                    <div className="w-6 h-10 border-2 border-[#F9F6F0]/50 rounded-full flex items-start justify-center p-1">
                        <div className="w-1.5 h-3 bg-[#F9F6F0]/50 rounded-full"></div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="pt-24 sm:pt-32 md:pt-40 pb-8 sm:pb-12 bg-[#F9F6F0]" data-testid="about-section">
                <div className="max-w-7xl mx-auto px-6 sm:px-12">
                    <div className="text-center mb-12 sm:mb-16">
                        <p className="overline mb-4">What This Is</p>
                        <h2 className="heading-2" data-testid="about-heading">{siteSettings?.about_heading || 'A space to come home to yourself.'}</h2>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <div className="body-text whitespace-pre-wrap">
                                {siteSettings?.about_body || 'Methode 4 Life is a school of Consciousness, not a self-help programme, not a therapy, not a spiritual trend.\n\nThe school offers a framework and different maps for understanding why you are the way you are, what it is protecting, and what it is calling you toward.'}
                            </div>
                        </div>
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&q=80"
                                alt="Spiritual gathering"
                                className="w-full rounded-sm shadow-xl"
                            />
                            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#5B2D8E]/10 rounded-sm -z-10"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dynamic Homepage Blocks (from admin builder) */}
            <HomeBlockRenderer blocks={homeBlocks} />

            {/* Admin-editable home intro — invisible until an admin fills it in */}
            <section className="bg-[#F9F6F0]" data-testid="home-intro-section">
                <div className="max-w-5xl mx-auto px-6 sm:px-12">
                    <EditablePageIntro pageKey="home" fallback={null} />
                </div>
            </section>

            {/* Mirror Circle Membership CTA — intro + button (cards live on /membership) */}
            <section id="membership" className="pt-8 sm:pt-12 pb-24 sm:pb-32 md:pb-40 bg-[#F9F6F0]" data-testid="membership-section">
                <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
                    <p className="overline mb-4">Mirror Circle</p>
                    <h2 className="heading-2 mb-6">Membership</h2>
                    <p className="body-text max-w-3xl mx-auto mb-12">
                        Everything in the school is available to everyone. Books, resources, tools, events —
                        open, at full price, to any who seek them. The Mirror Circle is for those who choose
                        to walk closer. Membership is not a gate — it is a choice to receive more, for less,
                        and to be held inside a living community.
                    </p>
                    <Link
                        to="/membership"
                        className="inline-block bg-[#5B2D8E] text-white hover:bg-[#4A2475] transition rounded-sm font-sans text-sm uppercase tracking-widest px-10 py-4"
                        data-testid="membership-cta"
                    >
                        More information about our memberships
                    </Link>
                </div>
            </section>

            {/* Pillar of the month — rotates based on current calendar month */}
            {featuredPillar && (
                <section className="py-20 sm:py-24 bg-white border-y border-[#E5E0D8]" data-testid="featured-pillar-section">
                    <div className="max-w-5xl mx-auto px-6 sm:px-12">
                        <div className="text-center mb-12">
                            <p className="overline mb-3">Mirror Circle Partnership</p>
                            <h2 className="heading-2">Meet this month's pillar</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 items-center">
                            <div className="aspect-square bg-gradient-to-br from-[#5B2D8E]/10 to-[#D8B4E2]/20 rounded-sm overflow-hidden flex items-center justify-center">
                                {featuredPillar.profile_image_url ? (
                                    <img src={resolveImageUrl(featuredPillar.profile_image_url)} alt={featuredPillar.name} className="w-full h-full object-cover" />
                                ) : (
                                    <span className="font-serif text-7xl text-[#5B2D8E]">{(featuredPillar.name || '?').charAt(0)}</span>
                                )}
                            </div>
                            <div className="md:col-span-2">
                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                    <h3 className="font-serif text-3xl text-[#1A1A2E]" data-testid="featured-pillar-name">{featuredPillar.name}</h3>
                                    {featuredPillar.is_founder && (
                                        <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2.5 py-1 bg-[#5B2D8E] text-white rounded-sm">
                                            Founder
                                        </span>
                                    )}
                                </div>
                                {splitSpecialties(featuredPillar.specialty).length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mb-4">
                                        {splitSpecialties(featuredPillar.specialty).map(tag => (
                                            <span
                                                key={tag}
                                                className="text-[11px] uppercase tracking-widest px-2.5 py-1 border border-[#5B2D8E]/30 text-[#5B2D8E] rounded-sm"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {(featuredPillar.city || featuredPillar.country) && (
                                    <p className="text-sm text-[#4A4A68] mb-4">
                                        {[featuredPillar.city, featuredPillar.country].filter(Boolean).join(', ')}
                                    </p>
                                )}
                                {featuredPillar.bio && (
                                    <p className="text-[#1A1A2E] mb-6 whitespace-pre-wrap line-clamp-5">{featuredPillar.bio}</p>
                                )}
                                <Link
                                    to={`/pillars/${featuredPillar.id}`}
                                    className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-[#5B2D8E] hover:gap-3 transition-all"
                                    data-testid="featured-pillar-link"
                                >
                                    View full profile <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Partnership Section */}
            <section className="py-24 bg-[#F2EBE5]" data-testid="partnership-section">
                <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
                    <div className="inline-flex items-center gap-2 mb-6">
                        <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                        <p className="overline">Mirror Circle Partnership</p>
                        <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <h3 className="heading-3 mb-6">Not open to the public</h3>
                    <p className="body-text mb-8 max-w-2xl mx-auto">
                        For the Weavers and Pillars of the school. Trainers, speakers, event hosts, contributors — 
                        those who run the show from inside. Paid or volunteer. Architect advantages + participation 
                        in organization and Special Events.
                    </p>
                    <p className="font-serif text-xl text-[#5B2D8E] mb-8">
                        Are you interested in becoming a Pillar of the school? Or are you an event organizer?
                    </p>

                    {/* Contact Form */}
                    <form onSubmit={handleContactSubmit} className="max-w-md mx-auto text-left" data-testid="contact-form">
                        <div className="space-y-4">
                            <input
                                type="text"
                                placeholder="Your name"
                                value={contactForm.name}
                                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                className="w-full"
                                required
                                data-testid="contact-name"
                            />
                            <input
                                type="email"
                                placeholder="Your email"
                                value={contactForm.email}
                                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                className="w-full"
                                required
                                data-testid="contact-email"
                            />
                            <textarea
                                placeholder="Tell us about yourself and your interest..."
                                rows={4}
                                value={contactForm.message}
                                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                className="w-full resize-none"
                                required
                                data-testid="contact-message"
                            />
                            <button
                                type="submit"
                                className="btn-primary w-full flex items-center justify-center gap-2"
                                disabled={submitting}
                                data-testid="contact-submit"
                            >
                                {submitting ? 'Sending...' : 'Get in Touch'}
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </form>
                </div>
            </section>

            <Footer />
        </div>
    );
}
